import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import React from 'react';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import { AccountData, AccountService, OrderPageData } from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { MaxStationsRestrictionTable } from '../../MaxStationsRestrictionTable';
import { OrderPagesMultiSelect } from '../../../userSettings/OrderPagesMultiSelect';
import { SharedResourcesAllocationTable } from './SharedResourcesAllocationTable';

type RestrictionsSettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
  account: AccountData;
};

export type RestrictionsFormValues = {
  availability_settings: {
    parallel_order_page_ids: string[];
  };
  shared_resources_allocations?: {
    automatic_dividable_resources: {
      resource_id: string;
      unit_capacity: number;
      total_units: number;
    }[];
  };
};

export const RestrictionsSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
  account,
}: RestrictionsSettingsSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();

  const validationSchema = yup.object().shape({
    availability_settings: yup.object().shape({
      parallel_order_page_ids: yup.array().of(yup.string()),
    }),
    shared_resources_allocations: yup.object().shape({
      automatic_dividable_resources: yup.array().of(
        yup.object().shape({
          resource_id: yup.string().required(),
          unit_capacity: yup
            .number()
            .min(1)
            .test('max-capacity', t('sharedResources.maxCapacity'), function (value, context) {
              const { resource_id } = context.parent;
              const maxCapacity = account.shared_resources?.automatic_dividable_resources?.find(
                (r) => r.id === resource_id,
              )?.unit_capacity;
              return !value || !maxCapacity || value <= maxCapacity;
            }),
          total_units: yup
            .number()
            .min(1)
            .test('max-units', t('sharedResources.maxCapacity'), function (value, context) {
              const { resource_id } = context.parent;
              const maxUnits = account.shared_resources?.automatic_dividable_resources?.find(
                (r) => r.id === resource_id,
              )?.total_units;
              return !value || !maxUnits || value <= maxUnits;
            }),
        }),
      ),
    }),
  });

  const initialValues: RestrictionsFormValues = {
    availability_settings: {
      parallel_order_page_ids: orderPage.availability_settings.parallel_order_page_ids || [],
    },
    shared_resources_allocations: {
      automatic_dividable_resources: orderPage.shared_resources_allocations?.automatic_dividable_resources || [],
    },
  };

  const handleSubmit = async (values: RestrictionsFormValues) => {
    try {
      posthog.capture('Editing settings', { section: 'RestrictionsSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            ...values,
            id: orderPage.id,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
      setIsUpdatedSnackOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ values, isSubmitting, dirty, handleSubmit, errors, touched, handleChange }) => {
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={handleSubmit}>
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={2}>
                <Grid item>
                  <MaxStationsRestrictionTable orderPage={orderPage} />
                </Grid>
                <Grid item>
                  <Typography fontWeight={700}>{t('parallelOrderPages.title')}</Typography>
                  <OrderPagesMultiSelect
                    exceptOrderPageId={orderPage.id}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    orderPagesFieldName="availability_settings.parallel_order_page_ids"
                    account={account}
                  />
                </Grid>
                {account.shared_resources && (
                  <Grid item>
                    <Typography fontWeight={700}>{t('sharedResources.title')}</Typography>
                    <SharedResourcesAllocationTable
                      values={values.shared_resources_allocations?.automatic_dividable_resources || []}
                      onChange={(newAllocations) => {
                        handleChange({
                          target: {
                            name: 'shared_resources_allocations.automatic_dividable_resources',
                            value: newAllocations,
                          },
                        });
                      }}
                      resources={account.shared_resources}
                      disabled={formIsDisabled}
                    />
                  </Grid>
                )}
              </Grid>
              <LoadingButton
                size="large"
                loading={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
                disabled={!dirty || formIsDisabled}>
                {t('editOrderPage.orderPageBufferSubmit')}
              </LoadingButton>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
